import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { toast } from 'sonner';

/**
 * Global error handler — surface query/mutation failures consistently
 * without spamming for benign aborts.
 */
function handleError(error: unknown, context: 'query' | 'mutation') {
  if (!error) return;
  const message =
    error instanceof Error ? error.message : 'An unexpected error occurred';

  // Don't toast aborted queries / network cancellations
  if (/abort|cancelled/i.test(message)) return;

  // eslint-disable-next-line no-console
  console.error(`[react-query:${context}]`, error);

  if (context === 'mutation') {
    toast.error(message);
  }
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000, // 1 minute — avoid refetch storms during nav
      gcTime: 5 * 60_000, // 5 minutes
      retry: (failureCount, error: unknown) => {
        // Don't retry 4xx — usually unrecoverable (auth, validation, not found)
        const status = (error as { status?: number; statusCode?: number })?.status
          ?? (error as { status?: number; statusCode?: number })?.statusCode;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 0,
    },
  },
  queryCache: new QueryCache({
    onError: (error) => handleError(error, 'query'),
  }),
  mutationCache: new MutationCache({
    onError: (error) => handleError(error, 'mutation'),
  }),
});
